import { User_userPermissions } from "@saleor/fragments/types/User";
import { CouponDealTypeEnum } from "@saleor/types/globalTypes";

import { CouponUnlockCodeBulkCreate_couponUnlockCodeBulkCreate_errors } from "./types/CouponUnlockCodeBulkCreate";

export const checkCouponPermissionAccess = (
  userPermissions: User_userPermissions[] | any[],
  permissionToCheck: string
) =>
  Boolean(
    userPermissions?.some(
      perm =>
        (typeof perm === "string" ? perm : perm?.code) === permissionToCheck
    )
  );

export const canManageCouponUsageLimitByDealType = (
  userPermissions: User_userPermissions[] | any[],
  dealType?: CouponDealTypeEnum | string | null
): boolean => {
  if (!checkCouponPermissionAccess(userPermissions, "MANAGE_COUPONS")) {
    return false;
  }

  const normalizedDealType =
    typeof dealType === "string" ? dealType.toUpperCase() : dealType;

  if (
    normalizedDealType === CouponDealTypeEnum.EVENTS ||
    normalizedDealType === "EVENTS"
  ) {
    return checkCouponPermissionAccess(
      userPermissions,
      "MANAGE_COUPON_USAGE_LIMIT_EVENTS"
    );
  }

  return checkCouponPermissionAccess(
    userPermissions,
    "MANAGE_COUPON_USAGE_LIMIT"
  );
};

export const hasCouponUsageLimitPermissionChanged = (
  prevPermissions: User_userPermissions[] | any[],
  nextPermissions: User_userPermissions[] | any[],
  dealType?: CouponDealTypeEnum | string | null
): boolean => {
  const prevCan = canManageCouponUsageLimitByDealType(
    prevPermissions,
    dealType
  );
  const nextCan = canManageCouponUsageLimitByDealType(
    nextPermissions,
    dealType
  );
  return prevCan !== nextCan;
};

export const hasAnyCouponUsageLimitPermissionChanged = (
  prevPermissions: User_userPermissions[] | any[],
  nextPermissions: User_userPermissions[] | any[]
): boolean => {
  const prevDeals = canManageCouponUsageLimitByDealType(
    prevPermissions,
    CouponDealTypeEnum.DEALS
  );
  const nextDeals = canManageCouponUsageLimitByDealType(
    nextPermissions,
    CouponDealTypeEnum.DEALS
  );
  const prevEvents = canManageCouponUsageLimitByDealType(
    prevPermissions,
    CouponDealTypeEnum.EVENTS
  );
  const nextEvents = canManageCouponUsageLimitByDealType(
    nextPermissions,
    CouponDealTypeEnum.EVENTS
  );
  return prevDeals !== nextDeals || prevEvents !== nextEvents;
};

const parseUsageLimit = (
  usageLimit: number | string | null | undefined
): number | null => {
  const parsed = Number(usageLimit);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
};

export const resolveCouponUsageLimitForCreate = (
  canManageCouponUsageLimit: boolean,
  usageLimit: number | string | null | undefined
): number => {
  if (!canManageCouponUsageLimit) {
    return 1;
  }
  return parseUsageLimit(usageLimit) ?? 1;
};

export const resolveCouponUsageLimitForUpdate = (
  canManageCouponUsageLimit: boolean,
  usageLimit: number | string | null | undefined,
  existingUsageLimit?: number | string | null | undefined
): number => {
  if (!canManageCouponUsageLimit) {
    return (
      parseUsageLimit(existingUsageLimit) ?? parseUsageLimit(usageLimit) ?? 1
    );
  }
  return parseUsageLimit(usageLimit) ?? 1;
};

export const resolveCouponUsageLimit = (
  canManageCouponUsageLimit: boolean,
  usageLimit: number | string | null | undefined
): number =>
  resolveCouponUsageLimitForCreate(canManageCouponUsageLimit, usageLimit);

// Function that reads the errors, and extract the existing unlock codes as an array
export const extractAlreadyExistsUnlockCodeError = (
  errors: CouponUnlockCodeBulkCreate_couponUnlockCodeBulkCreate_errors[]
): string[] =>
  errors.reduce((existingUnlockCodesAcc: string[], errCurr) => {
    const splittedErrorMsg = errCurr.message.split("'");
    if (
      splittedErrorMsg.length === 3 &&
      splittedErrorMsg[0] === "Unlock code " &&
      splittedErrorMsg[2] === " already exists."
    ) {
      // Identify if the error is about the existing unlock code, then extract the existing unlock code
      existingUnlockCodesAcc.push(splittedErrorMsg[1]);
    }
    return existingUnlockCodesAcc;
  }, []);
